import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, PlusCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { workspaceAdminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const flattenTree = (nodes = [], level = 0, rows = []) => {
  nodes.forEach((node) => {
    rows.push({ ...node, _level: level });
    flattenTree(node.children || [], level + 1, rows);
  });
  return rows;
};

const parseCsvValues = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const toggleSelection = (values = [], nextValue) => {
  const normalized = String(nextValue || '').trim();
  if (!normalized) return values;
  return values.includes(normalized)
    ? values.filter((value) => value !== normalized)
    : [...values, normalized];
};

const SubTenantCreationPanel = ({
  mode = 'tenant',
  title,
  description,
  onCreated,
  defaultParentTenantId = null,
}) => {
  const { user } = useAuth();
  const isSuperadmin = mode === 'superadmin' || user?.role === 'superadmin';
  const canManageChildTenants = isSuperadmin || (
    Boolean(user?.canCreateChildTenants) &&
    !user?.parentTenantId &&
    ['workspace_admin', 'hr_admin'].includes(String(user?.role || '').toLowerCase())
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const [billingAccounts, setBillingAccounts] = useState([]);
  const [createdAdminCredentials, setCreatedAdminCredentials] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_password: '',
    parent_tenant_id: defaultParentTenantId ? String(defaultParentTenantId) : '',
    isolation_level: 'shared',
    billing_account_id: '',
    billing_model: 'parent_paid',
    can_create_child_tenants: false,
    allowed_modules: [],
    permission_group_keys: [],
    role_templates: [],
    max_users: '',
    max_employees: '',
    max_storage_gb: '',
    max_api_requests: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const requests = [isSuperadmin ? workspaceAdminAPI.getTenantHierarchy() : workspaceAdminAPI.getTenantTreePublic()];
      if (isSuperadmin) {
        requests.push(workspaceAdminAPI.listBillingAccounts());
      }
      const [hierarchyRes, billingRes] = await Promise.all(requests);
      const nextHierarchy = Array.isArray(hierarchyRes?.data) ? hierarchyRes.data : [];
      setHierarchy(nextHierarchy);
      setBillingAccounts(Array.isArray(billingRes?.data) ? billingRes.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load tenant hierarchy'));
    } finally {
      setLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const tenantOptions = useMemo(() => flattenTree(hierarchy), [hierarchy]);

  useEffect(() => {
    const fallbackParentId = defaultParentTenantId || user?.tenantId || tenantOptions[0]?.id || '';
    if (!formData.parent_tenant_id && fallbackParentId) {
      setFormData((prev) => ({ ...prev, parent_tenant_id: String(fallbackParentId) }));
    }
  }, [defaultParentTenantId, formData.parent_tenant_id, tenantOptions, user?.tenantId]);

  const selectedParent = useMemo(
    () => tenantOptions.find((row) => String(row.id) === String(formData.parent_tenant_id)),
    [tenantOptions, formData.parent_tenant_id]
  );
  const availableModules = useMemo(
    () => Array.isArray(user?.moduleAccess) ? user.moduleAccess : [],
    [user?.moduleAccess]
  );
  const availablePermissionGroups = useMemo(
    () => Array.isArray(user?.permissionGroups) ? user.permissionGroups : [],
    [user?.permissionGroups]
  );
  const availableRoleTemplates = useMemo(
    () => Object.keys(user?.rolePermissionsMap || {}).sort(),
    [user?.rolePermissionsMap]
  );

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.slug.trim() || !formData.parent_tenant_id) {
      toast.error('Name, slug, and parent tenant are required');
      return;
    }
    try {
      setSaving(true);
      setCreatedAdminCredentials(null);
      const requestData = {
        name: formData.name.trim(),
        slug: formData.slug.trim().toLowerCase(),
        admin_email: formData.admin_email.trim() || null,
        admin_password: formData.admin_password || null,
        parent_tenant_id: Number(formData.parent_tenant_id),
        isolation_level: formData.isolation_level,
        billing_account_id: formData.billing_account_id ? Number(formData.billing_account_id) : null,
        billing_model: formData.billing_model,
        can_create_child_tenants: false,
        allowed_modules: formData.allowed_modules,
        permission_group_keys: formData.permission_group_keys,
        role_templates: formData.role_templates,
        max_users: formData.max_users ? Number(formData.max_users) : null,
        max_employees: formData.max_employees ? Number(formData.max_employees) : null,
        max_storage_gb: formData.max_storage_gb ? Number(formData.max_storage_gb) : null,
        max_api_requests: formData.max_api_requests ? Number(formData.max_api_requests) : null,
      };
      const response = isSuperadmin
        ? await workspaceAdminAPI.createSubtenant(Number(formData.parent_tenant_id), requestData)
        : await workspaceAdminAPI.createChildTenant(Number(formData.parent_tenant_id), requestData);
      const createdTenant = response?.data || {};
      if (createdTenant?.tenant_admin_email) {
        setCreatedAdminCredentials({
          email: createdTenant.tenant_admin_email,
          temporaryPassword: createdTenant.tenant_admin_temporary_password || '',
          usedProvidedPassword: Boolean(formData.admin_email.trim() && formData.admin_password),
        });
      }
      toast.success('Tenant created');
      setFormData((prev) => ({
        ...prev,
        name: '',
        slug: '',
        admin_email: '',
        admin_password: '',
        allowed_modules: [],
        permission_group_keys: [],
        role_templates: [],
        max_users: '',
        max_employees: '',
        max_storage_gb: '',
        max_api_requests: '',
      }));
      await loadData();
      onCreated?.();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create tenant'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading tenant setup...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/15 shadow-sm">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>{title || 'Create Tenant'}</CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {description || 'Create a tenant under a parent tenant using the standard tenant model and governance settings.'}
              </p>
            </div>
            <Badge variant="outline" className="font-medium">
              {isSuperadmin ? 'Superadmin' : 'Tenant Admin'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="rounded-lg border bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <p className="text-sm font-medium">Parent Tenant</p>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {selectedParent
                ? `New tenant will be created under ${selectedParent.name}.`
                : 'Select the parent tenant that will own this tenant.'}
            </p>
          </div>

          {!isSuperadmin ? (
            <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
              {canManageChildTenants
                ? 'Child tenant access is limited to the modules, role templates, permission groups, and limits available to your current tenant plan.'
                : 'Child tenant creation is not enabled for this tenant or current role.'}
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="North Region HR"
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="north-region-hr"
              />
            </div>
            <div>
              <Label>Admin Email</Label>
              <Input
                type="email"
                value={formData.admin_email}
                onChange={(e) => setFormData((prev) => ({ ...prev, admin_email: e.target.value }))}
                placeholder="admin@north-region-hr.com"
              />
            </div>
            <div>
              <Label>Admin Password</Label>
              <Input
                type="password"
                value={formData.admin_password}
                onChange={(e) => setFormData((prev) => ({ ...prev, admin_password: e.target.value }))}
                placeholder="Leave blank to auto-generate"
              />
            </div>
            <div>
              <Label>Parent Tenant</Label>
              <Select
                value={formData.parent_tenant_id || '_none'}
                onValueChange={(value) => setFormData((prev) => ({ ...prev, parent_tenant_id: value === '_none' ? '' : value }))}
                disabled={!isSuperadmin}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Select parent tenant</SelectItem>
                  {tenantOptions.map((row) => (
                    <SelectItem key={row.id} value={String(row.id)}>
                      {`${'-- '.repeat(row._level)}${row.name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Isolation</Label>
              <Select value={formData.isolation_level} onValueChange={(value) => setFormData((prev) => ({ ...prev, isolation_level: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">shared</SelectItem>
                  <SelectItem value="isolated">isolated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isSuperadmin ? (
              <>
                <div>
                  <Label>Billing Account</Label>
                  <Select
                    value={formData.billing_account_id || '_none'}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, billing_account_id: value === '_none' ? '' : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {billingAccounts.map((row) => (
                        <SelectItem key={row.id} value={String(row.id)}>
                          {row.account_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Billing Model</Label>
                  <Select value={formData.billing_model} onValueChange={(value) => setFormData((prev) => ({ ...prev, billing_model: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parent_paid">parent_paid</SelectItem>
                      <SelectItem value="subtenant_paid">tenant_paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : null}
            {isSuperadmin ? (
              <>
                <div>
                  <Label>Allowed Modules</Label>
                  <Input
                    value={(formData.allowed_modules || []).join(', ')}
                    onChange={(e) => setFormData((prev) => ({ ...prev, allowed_modules: parseCsvValues(e.target.value) }))}
                    placeholder="hr, attendance, payroll"
                  />
                </div>
                <div>
                  <Label>Permission Groups</Label>
                  <Input
                    value={(formData.permission_group_keys || []).join(', ')}
                    onChange={(e) => setFormData((prev) => ({ ...prev, permission_group_keys: parseCsvValues(e.target.value) }))}
                    placeholder="hr, reports"
                  />
                </div>
                <div>
                  <Label>Role Templates</Label>
                  <Input
                    value={(formData.role_templates || []).join(', ')}
                    onChange={(e) => setFormData((prev) => ({ ...prev, role_templates: parseCsvValues(e.target.value) }))}
                    placeholder="workspace_admin, hr_admin"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-2">
                  <Label>Allowed Modules</Label>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    {availableModules.map((moduleKey) => (
                      <label key={moduleKey} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.allowed_modules.includes(moduleKey)}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              allowed_modules: toggleSelection(prev.allowed_modules, moduleKey),
                            }))
                          }
                        />
                        <span>{moduleKey}</span>
                      </label>
                    ))}
                    {availableModules.length === 0 ? <p className="text-sm text-muted-foreground">No owned modules available.</p> : null}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>Permission Groups</Label>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    {availablePermissionGroups.map((group) => (
                      <label key={group.group_key} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.permission_group_keys.includes(group.group_key)}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              permission_group_keys: toggleSelection(prev.permission_group_keys, group.group_key),
                            }))
                          }
                        />
                        <span>{group.display_name || group.group_key}</span>
                      </label>
                    ))}
                    {availablePermissionGroups.length === 0 ? <p className="text-sm text-muted-foreground">No owned permission groups available.</p> : null}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label>Role Templates</Label>
                  <div className="mt-2 grid gap-2 md:grid-cols-2">
                    {availableRoleTemplates.map((roleKey) => (
                      <label key={roleKey} className="flex items-center gap-2 rounded-md border p-2 text-sm">
                        <input
                          type="checkbox"
                          checked={formData.role_templates.includes(roleKey)}
                          onChange={() =>
                            setFormData((prev) => ({
                              ...prev,
                              role_templates: toggleSelection(prev.role_templates, roleKey),
                            }))
                          }
                        />
                        <span>{roleKey}</span>
                      </label>
                    ))}
                    {availableRoleTemplates.length === 0 ? <p className="text-sm text-muted-foreground">No owned role templates available.</p> : null}
                  </div>
                </div>
              </>
            )}
            <div>
              <Label>Max Users</Label>
              <Input
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData((prev) => ({ ...prev, max_users: e.target.value }))}
                placeholder="25"
              />
            </div>
            <div>
              <Label>Max Employees</Label>
              <Input
                type="number"
                value={formData.max_employees}
                onChange={(e) => setFormData((prev) => ({ ...prev, max_employees: e.target.value }))}
                placeholder="100"
              />
            </div>
            <div>
              <Label>Max Storage GB</Label>
              <Input
                type="number"
                value={formData.max_storage_gb}
                onChange={(e) => setFormData((prev) => ({ ...prev, max_storage_gb: e.target.value }))}
                placeholder="10"
              />
            </div>
            <div>
              <Label>Max API Requests</Label>
              <Input
                type="number"
                value={formData.max_api_requests}
                onChange={(e) => setFormData((prev) => ({ ...prev, max_api_requests: e.target.value }))}
                placeholder="50000"
              />
            </div>
          </div>

          {createdAdminCredentials ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
              <p className="font-medium text-foreground">Tenant Admin Login</p>
              <p className="mt-1 text-muted-foreground">Email: {createdAdminCredentials.email}</p>
              {createdAdminCredentials.temporaryPassword ? (
                <p className="mt-1 text-muted-foreground">
                  Temporary Password: {createdAdminCredentials.temporaryPassword}
                </p>
              ) : (
                <p className="mt-1 text-muted-foreground">The tenant was created with the password you entered.</p>
              )}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button onClick={handleCreate} disabled={saving || !canManageChildTenants}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Create Tenant
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubTenantCreationPanel;
